/**
 * Workspace context for NaraHub.
 * Manages active workspace state and workspace switching.
 * Supports URL-based workspace selection via handle.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/AuthContext";
import { getWorkspaces, type Workspace } from "../services/naraDoor";
import { setCurrentWorkspaceId } from "../lib/workspaceStore";
import i18n from "../i18n";

interface WorkspaceContextType {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  isLoading: boolean;
  error: string | null;
  /** Signed in, no seat, and the server did not make one because they had a seat: they were removed */
  noAccess: boolean;
  /** Handle from URL that couldn't be matched to a workspace */
  invalidHandle: string | null;
  switchWorkspace: (workspaceId: string) => void;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined
);

const NO_WORKSPACES: Workspace[] = [];

const ACTIVE_WORKSPACE_KEY = "narahub-active-workspace-id";

// Mock data for development mode
const DEV_MOCK_WORKSPACES: Workspace[] = [
  {
    id: "dev-workspace-1",
    name: "Development Workspace",
    handle: "dev-workspace",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

interface WorkspaceProviderProps {
  children: ReactNode;
  /** Workspace handle from URL parameter (optional) */
  workspaceHandle?: string;
}

export function WorkspaceProvider({ children, workspaceHandle }: WorkspaceProviderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    () => {
      const saved = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
      // Sync workspace store immediately so API calls have the ID
      if (saved) setCurrentWorkspaceId(saved);
      return saved;
    }
  );
  const [invalidHandle, setInvalidHandle] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["workspaces", user?.uid],
    // In dev mode without a user, use mock data
    enabled: !!user || import.meta.env.DEV,
    queryFn: async (): Promise<{ workspaces: Workspace[]; noAccess: boolean }> => {
      if (!user) return { workspaces: DEV_MOCK_WORKSPACES, noAccess: false };
      try {
        // The server gives a user with no workspace their own.
        // The invite page redeems an invite as the first seat, so it must not get a bootstrapped one.
        const response = await getWorkspaces({ skipBootstrap: window.location.pathname.startsWith("/invite") });
        if (response.workspaceBootstrapped) await user.getIdToken(true); // the new seat reaches the token claims
        const list = response.workspaces || [];
        // Has had a seat but has none now: removed since.
        return { workspaces: list, noAccess: !!response.workspaceBootstrapped && !list.length };
      } catch (err) {
        console.error("Failed to fetch workspaces:", err);
        // In dev mode, fall back to mock data on error
        if (import.meta.env.DEV) return { workspaces: DEV_MOCK_WORKSPACES, noAccess: false };
        throw err;
      }
    },
  });

  const workspaces = query.data?.workspaces ?? NO_WORKSPACES;
  const noAccess = query.data?.noAccess ?? false;
  const isLoading = query.isLoading;
  const error = query.isError ? i18n.t("workspaceLoad.error") : null;

  // Handle URL-based workspace selection - URL is the source of truth
  useEffect(() => {
    if (!workspaces.length || isLoading) return;

    if (workspaceHandle) {
      // Find workspace by handle from URL
      const matchedWorkspace = workspaces.find(
        (ws) => ws.handle === workspaceHandle
      );

      if (matchedWorkspace) {
        // Valid handle - select this workspace
        setInvalidHandle(null);
        if (matchedWorkspace.id !== activeWorkspaceId) {
          setActiveWorkspaceId(matchedWorkspace.id);
          // Update the workspace store for API client access
          setCurrentWorkspaceId(matchedWorkspace.id);
          // Still persist to localStorage for workspace switcher memory
          localStorage.setItem(ACTIVE_WORKSPACE_KEY, matchedWorkspace.id);
        }
      } else {
        // Invalid handle - mark as invalid and clear workspace
        setInvalidHandle(workspaceHandle);
        setActiveWorkspaceId(null);
        setCurrentWorkspaceId(null);
      }
    } else {
      // No handle in URL - use localStorage or first workspace for backward compatibility
      setInvalidHandle(null);
      if (!activeWorkspaceId && workspaces.length > 0) {
        const savedId = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
        const savedWorkspace = savedId ? workspaces.find(ws => ws.id === savedId) : null;
        const targetWorkspace = savedWorkspace || workspaces[0];
        setActiveWorkspaceId(targetWorkspace.id);
        setCurrentWorkspaceId(targetWorkspace.id);
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, targetWorkspace.id);
      }
    }
  }, [workspaces, workspaceHandle, activeWorkspaceId, isLoading]);

  // Validate that activeWorkspaceId is in the workspaces list (when no URL handle)
  useEffect(() => {
    if (workspaceHandle) return; // Skip validation when URL controls workspace

    if (
      activeWorkspaceId &&
      workspaces.length > 0 &&
      !workspaces.find((ws) => ws.id === activeWorkspaceId)
    ) {
      // Active workspace no longer exists, switch to first available
      const firstWorkspace = workspaces[0];
      setActiveWorkspaceId(firstWorkspace.id);
      setCurrentWorkspaceId(firstWorkspace.id);
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, firstWorkspace.id);
    }
  }, [workspaces, activeWorkspaceId, workspaceHandle]);

  const activeWorkspace =
    workspaces.find((ws) => ws.id === activeWorkspaceId) || null;

  const switchWorkspace = useCallback((workspaceId: string) => {
    const workspace = workspaces.find((ws) => ws.id === workspaceId);
    if (!workspace) return;

    setActiveWorkspaceId(workspaceId);
    setCurrentWorkspaceId(workspaceId);
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId);

    // Navigate to the new workspace's dashboard
    if (workspace.handle) {
      navigate(`/ws/${workspace.handle}/dashboard`);
    }
  }, [workspaces, navigate]);

  const { refetch } = query;
  const refreshWorkspaces = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        isLoading,
        error,
        noAccess,
        invalidHandle,
        switchWorkspace,
        refreshWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
